import Pbf from 'pbf';

let keys: string[] | null, values: unknown[], lengths: number[] | null, dim: number, e: number;

const geometryTypes: GeoJSON.GeoJsonGeometryTypes[] = [
    'Point',
    'MultiPoint',
    'LineString',
    'MultiLineString',
    'Polygon',
    'MultiPolygon',
    'GeometryCollection',
];

export function decode(pbf: Pbf): GeoJSON.GeoJSON {
    dim = 2;
    e = Math.pow(10, 6);
    lengths = null;

    keys = [];
    values = [];
    const obj = pbf.readFields(readDataField, {});
    keys = null;

    return obj as GeoJSON.GeoJSON;
}

function readDataField(tag: number, obj: Partial<GeoJSON.GeoJSON>, pbf: Pbf) {
    if (tag === 1) {
        keys!.push(pbf.readString());
    } else if (tag === 2) {
        dim = pbf.readVarint();
    } else if (tag === 3) {
        e = Math.pow(10, pbf.readVarint());
    } else if (tag === 4) {
        readFeatureCollection(pbf, obj as GeoJSON.FeatureCollection);
    } else if (tag === 5) {
        readFeature(pbf, obj as GeoJSON.Feature);
    } else if (tag === 6) {
        readGeometry(pbf, obj as GeoJSON.Geometry);
    }
}

function readFeatureCollection(pbf: Pbf, obj: GeoJSON.FeatureCollection) {
    obj.type = 'FeatureCollection';
    obj.features = [];
    return pbf.readMessage(readFeatureCollectionField, obj);
}

function readFeature(pbf: Pbf, feature: Partial<GeoJSON.Feature>) {
    feature.type = 'Feature';
    const f = pbf.readMessage(readFeatureField, feature as GeoJSON.Feature);
    if (!('geometry' in f)) {
        (f as Record<string, unknown>).geometry = null;
    }
    return f;
}

function readGeometry(pbf: Pbf, geom: Partial<GeoJSON.Geometry>) {
    geom.type = 'Point';
    return pbf.readMessage(readGeometryField, geom as GeoJSON.Geometry);
}

function readFeatureCollectionField(tag: number, obj: GeoJSON.FeatureCollection, pbf: Pbf) {
    if (tag === 1) {
        obj.features.push(readFeature(pbf, {}));
    } else if (tag === 13) {
        values.push(readValue(pbf));
    } else if (tag === 15) {
        readProps(pbf, obj);
    }
}

function readFeatureField(tag: number, feature: GeoJSON.Feature, pbf: Pbf) {
    if (tag === 1) {
        feature.geometry = readGeometry(pbf, {});
    } else if (tag === 11) {
        feature.id = pbf.readString();
    } else if (tag === 12) {
        feature.id = pbf.readSVarint();
    } else if (tag === 13) {
        values.push(readValue(pbf));
    } else if (tag === 14) {
        feature.properties = readProps(pbf, {});
    } else if (tag === 15) {
        readProps(pbf, feature);
    }
}

function readGeometryField(tag: number, geom: GeoJSON.Geometry, pbf: Pbf) {
    if (tag === 1) {
        geom.type = geometryTypes[pbf.readVarint()]!;
    } else if (tag === 2) {
        lengths = pbf.readPackedVarint();
    } else if (tag === 3) {
        readCoords(geom as Exclude<GeoJSON.Geometry, GeoJSON.GeometryCollection>, pbf, geom.type);
    } else if (tag === 4) {
        (geom as Partial<GeoJSON.GeometryCollection>).geometries ||= [];
        (geom as GeoJSON.GeometryCollection).geometries.push(readGeometry(pbf, {}));
    } else if (tag === 13) {
        values.push(readValue(pbf));
    } else if (tag === 15) {
        readProps(pbf, geom);
    }
}

function readCoords(
    geom: Exclude<GeoJSON.Geometry, GeoJSON.GeometryCollection>,
    pbf: Pbf,
    type: GeoJSON.GeoJsonGeometryTypes,
) {
    if (type === 'Point') {
        geom.coordinates = readPoint(pbf);
    } else if (type === 'MultiPoint') {
        geom.coordinates = readLine(pbf, true);
    } else if (type === 'LineString') {
        geom.coordinates = readLine(pbf);
    } else if (type === 'MultiLineString') {
        geom.coordinates = readMultiLine(pbf);
    } else if (type === 'Polygon') {
        geom.coordinates = readMultiLine(pbf, true);
    } else if (type === 'MultiPolygon') {
        geom.coordinates = readMultiPolygon(pbf);
    }
}

function readValue(pbf: Pbf) {
    const end = pbf.readVarint() + pbf.pos;
    let value = null;

    while (pbf.pos < end) {
        const val = pbf.readVarint(),
            tag = val >> 3;

        if (tag === 1) {
            value = pbf.readString();
        } else if (tag === 2) {
            value = pbf.readDouble();
        } else if (tag === 3) {
            value = pbf.readVarint();
        } else if (tag === 4) {
            value = -pbf.readVarint();
        } else if (tag === 5) {
            value = pbf.readBoolean();
        }
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        else if (tag === 6) {
            value = JSON.parse(pbf.readString());
        }
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return value;
}

function readProps(pbf: Pbf, props: GeoJSON.GeoJsonProperties) {
    const end = pbf.readVarint() + pbf.pos;
    while (pbf.pos < end) {
        props![keys![pbf.readVarint()]!] = values[pbf.readVarint()];
    }
    values = [];
    return props;
}

function readPoint(pbf: Pbf) {
    const end = pbf.readVarint() + pbf.pos,
        coords = [];
    while (pbf.pos < end) {
        coords.push(pbf.readSVarint() / e);
    }
    return coords;
}

function readLinePart(pbf: Pbf, end: number, len?: number | null, closed?: boolean) {
    let i = 0,
        p,
        d;
    const coords: number[][] = [];

    const prevP = [];
    for (d = 0; d < dim; d++) {
        prevP[d] = 0;
    }

    while (len ? i < len : pbf.pos < end) {
        p = [];
        for (d = 0; d < dim; d++) {
            prevP[d]! += pbf.readSVarint();
            p[d] = prevP[d]! / e;
        }
        coords.push(p);
        i++;
    }
    if (closed) {
        coords.push(coords[0]!);
    }

    return coords;
}

function readLine(pbf: Pbf, closed?: boolean) {
    return readLinePart(pbf, pbf.readVarint() + pbf.pos, null, closed);
}

function readMultiLine(pbf: Pbf, closed?: boolean) {
    const end = pbf.readVarint() + pbf.pos;
    if (!lengths) {
        return [readLinePart(pbf, end, null, closed)];
    }

    const coords = [];
    for (const length of lengths) {
        coords.push(readLinePart(pbf, end, length, closed));
    }
    lengths = null;
    return coords;
}

function readMultiPolygon(pbf: Pbf) {
    const end = pbf.readVarint() + pbf.pos;
    if (!lengths) {
        return [[readLinePart(pbf, end, null, true)]];
    }

    const coords = [];
    let j = 1;
    for (let i = 0; i < lengths[0]!; i++) {
        const rings = [];
        for (let k = 0; k < lengths[j]!; k++) {
            rings.push(readLinePart(pbf, end, lengths[j + 1 + k], true));
        }
        j += lengths[j]! + 1;
        coords.push(rings);
    }
    lengths = null;
    return coords;
}
